/**
 * @file src/room/chat/chatPanelState.js
 * @description Whether the room's chat is open, and how many lines have arrived unread.
 *
 * The chat rail and the score transport bar are two separate React roots — the rail hangs at the
 * top right, the bar spans the room's chrome — so the bubble that OPENS the chat and the panel that
 * shows it cannot share component state. This is the seam between them: a module singleton
 * with the same set/get/subscribe shape the room store, the roster and the score seam already use,
 * which is also why neither root reaches for a window global.
 */

const STORAGE_KEY = 'orbiters:room-chat-open';

/**
 * Open on arrival: the rail only ever mounts in room mode, and a first arrival on a device means the
 * waiting room — the one screen whose entire point is the people you are about to play with. Asking
 * everyone to find chat before they can say "ready?" is the wrong order.
 *
 * It does NOT stay that way. The room client closes it on `room:started`: the Playground is about
 * the decks, and a rail parked over them is something every player has to dismiss before they can
 * begin. Closed, never unmounted — the bubble stays, and reopening is one press.
 *
 * That close is exactly why the state has to outlive the tab. A reload is not an arrival: the tab
 * rejoins a room that is already playing, so `room:started` has long since fired and will not fire
 * again. With the flag living in module scope alone, every refresh re-initialised to the arrival
 * default and parked the rail back over the decks.
 */
/**
 * Every touch of storage sits INSIDE the try, the `window.localStorage` lookup included. Reading
 * that property is itself a throw in an embed whose host has third-party site data blocked — and
 * this module is imported by the room client, so a throw here is not a lost preference, it is the
 * whole room failing to load. Default open: a first arrival on a device is the waiting room.
 */
function readStored() {
  try {
    if (typeof window === 'undefined') return true;
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    return raw === null || raw === undefined ? true : raw === 'true';
  } catch {
    return true;
  }
}

/** Best-effort, per device. A browser that refuses storage still gets a working chat. */
function persist(value) {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage?.setItem(STORAGE_KEY, String(value));
  } catch {
    // noop — persistence is best-effort
  }
}

let open = readStored();
let unread = 0;
const listeners = new Set();

function notify() {
  for (const listener of listeners) {
    try {
      listener();
    } catch (error) {
      console.warn('[room] chat panel listener failed', error);
    }
  }
}

export function isChatPanelOpen() {
  return open;
}

/** Unread lines while the panel is closed. Always 0 while it is open — reading clears it. */
export function chatPanelUnread() {
  return unread;
}

/**
 * The one place the open flag moves, so it is the one place that remembers. Whatever moved it is
 * remembered the same way — the player's own press and the room client's `room:started` close alike.
 * There is no special case for "the app closed it rather than the player": a rail the player found
 * closed when the decks appeared is a rail they expect closed when they come back.
 */
export function setChatPanelOpen(next) {
  const value = Boolean(next);
  if (value === open) return;
  open = value;
  if (open) unread = 0;
  persist(open);
  notify();
}

export function toggleChatPanel() {
  setChatPanelOpen(!open);
}

/**
 * Adopt what the device remembers. Arriving at a room is the moment that question is asked, and it
 * is asked again on every arrival rather than only at import: a tab that leaves one room and joins
 * another never reloads this module, so without this the live value would still be whatever the
 * teardown left behind.
 */
export function restoreChatPanelFromMemory() {
  const remembered = readStored();
  if (remembered === open) return;
  open = remembered;
  if (open) unread = 0;
  notify();
}

/**
 * Open the rail without remembering it — for a room with no way back INTO chat.
 *
 * The rail hides its own trigger, so the only way in is the bubble in the score bar, and that bar
 * only mounts for a room that is both playing and scored. On any other screen a closed rail is not
 * closed, it is gone. So those screens force it open.
 *
 * The live value only. It deliberately does not write: the screen is answering for itself, not the
 * player choosing anything, and a remembered preference has to survive passing through it.
 */
export function openChatPanelWithoutRemembering() {
  if (open) return;
  open = true;
  unread = 0;
  notify();
}

/** Reported by the rail, which is the only thing that knows what has been read. */
export function setChatPanelUnread(count) {
  const value = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  if (value === unread) return;
  unread = value;
  notify();
}

export function subscribeChatPanel(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Room teardown / tests.
 *
 * Forces the live value open and deliberately does NOT write to storage. The next room starts on its
 * waiting screen, which has no bubble — the rail must be open there or chat is unreachable. And a
 * teardown is not the player closing anything, so it has nothing to say about how they want to find
 * the rail next time: overwriting the memory here would erase a real preference with a housekeeping
 * step.
 */
export function resetChatPanel() {
  open = true;
  unread = 0;
  notify();
}
